import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/auth';
import { useAdminCheck } from '@/hooks/useAdminCheck';

/**
 * Route guard for admin-only pages.
 * Decision tree:
 * - !authResolved → spinner (wait for session check)
 * - authResolved && !hasSession → redirect to login
 * - hasSession && adminLoading → spinner (wait for RPC)
 * - hasSession && !isAdmin → redirect home
 * - hasSession && isAdmin → render children
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { authResolved, hasSession } = useAuthContext();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const location = useLocation();

  // 1. Auth not yet resolved → show spinner
  if (!authResolved) {
    return <LoadingSpinner />;
  }

  // 2. No session → redirect to login
  if (!hasSession) {
    return <Navigate to="/auth/login" replace state={{ from: location }} />;
  }

  // 3. Session exists but admin check still loading → spinner
  if (adminLoading) {
    return <LoadingSpinner />;
  }

  // 4. Not admin → redirect home
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // 5. Admin → render
  return <>{children}</>;
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Inizializzazione pannello admin...</p>
      </div>
    </div>
  );
}
